import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { useClients } from "./api";
import { ClientForm } from "./ClientForm";
import { CLIENT_STATUS_LABEL } from "@/types";
import { ago } from "@/lib/format";
import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { QueryError } from "@/components/shared/QueryError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const STATUS_VARIANT = { active: "default", suspended: "destructive", closed: "secondary" } as const;

export default function ClientsPage() {
  const nav = useNavigate();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [creating, setCreating] = useState(false);
  const { data: clients, isLoading, error } = useClients({ search, status });

  return (
    <div>
      <PageHeader
        title="Clients"
        description="Les entreprises qui t'achètent des leads, leurs interlocuteurs et leurs conditions."
        actions={<Button onClick={() => setCreating(true)}><Plus className="mr-2 h-4 w-4" />Nouveau client</Button>}
      />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input className="w-72 pl-8" placeholder="Rechercher un client…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="active">Actifs</SelectItem>
            <SelectItem value="suspended">Suspendus</SelectItem>
            <SelectItem value="closed">Fermés</SelectItem>
          </SelectContent>
        </Select>
        {clients && <span className="ml-auto text-sm text-muted-foreground">{clients.length} client{clients.length > 1 ? "s" : ""}</span>}
      </div>

      {error ? <QueryError error={error} /> : isLoading ? <p className="text-sm text-muted-foreground">Chargement…</p> : !clients?.length ? (
        <EmptyState
          title={search || status !== "all" ? "Aucun client ne correspond" : "Aucun client pour l'instant"}
          description={search || status !== "all" ? "Essaie d'autres filtres." : "La base démarre vide : chaque nouveau client passe par ici."}
          action={!search && status === "all" ? <Button onClick={() => setCreating(true)}>Créer le premier client</Button> : undefined}
        />
      ) : (
        <div className="glass overflow-hidden rounded-2xl">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead><TableHead>Pays</TableHead><TableHead>Devise</TableHead>
                <TableHead>Conditions</TableHead><TableHead>Dernière activité</TableHead><TableHead>Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.map((c) => (
                <TableRow key={c.id} className="cursor-pointer" onClick={() => nav(`/clients/${c.id}`)}>
                  <TableCell>
                    <div className="font-medium">{c.legal_name}</div>
                    {c.trade_name && <div className="text-xs text-muted-foreground">{c.trade_name}</div>}
                  </TableCell>
                  <TableCell>{c.country}</TableCell>
                  <TableCell>{c.currency}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">acompte {Number(c.deposit_pct)} % · {c.payment_days} j</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{c.last_activity_at ? ago(c.last_activity_at) : "aucune"}</TableCell>
                  <TableCell><Badge variant={STATUS_VARIANT[c.status]}>{CLIENT_STATUS_LABEL[c.status]}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto pb-0">
          <DialogHeader><DialogTitle>Nouveau client</DialogTitle></DialogHeader>
          <ClientForm onDone={(id) => { setCreating(false); nav(`/clients/${id}`); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
